/**
 * 登录记录页
 *
 * 品牌筛选 + 服务端分页 DataGrid，展示每个品牌的员工/老板登录记录。
 * 数据来源：GET /admin/login-logs
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material'
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { AdminLayout } from '@/components/AdminLayout'
import { getBrands, getLoginLogs, type LoginLogItem } from '@/api/services'
import type { Brand } from '@chaoyinli/shared'
import { formatDate } from '@/utils/format'

/** 角色中文映射 */
const ROLE_LABELS: Record<LoginLogItem['role'], string> = {
  employee: '员工',
  boss: '老板',
  '': '未知',
}

export function LoginLogsPage() {
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandFilter, setBrandFilter] = useState('')
  const [rows, setRows] = useState<LoginLogItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })

  // 加载品牌下拉
  useEffect(() => {
    getBrands()
      .then(setBrands)
      .catch(() => setBrands([]))
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getLoginLogs({
        brand_id: brandFilter || undefined,
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      })
      setRows(data.items)
      setTotal(data.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载登录记录失败')
    } finally {
      setLoading(false)
    }
  }, [brandFilter, paginationModel])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const columns: GridColDef[] = [
    {
      field: 'employee_name',
      headerName: '员工',
      width: 140,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'brand_name',
      headerName: '品牌',
      width: 160,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'ip',
      headerName: 'IP',
      width: 140,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'device',
      headerName: '设备/系统',
      width: 220,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'login_at',
      headerName: '登录时间',
      width: 200,
      renderCell: (params) => (params.value ? formatDate(params.value) : '-'),
    },
    {
      field: 'success',
      headerName: '结果',
      width: 100,
      renderCell: (params) => {
        const success = params.value as boolean
        const color = success ? '#4CAF50' : '#F44336'
        return (
          <Chip
            label={success ? '成功' : '失败'}
            size="small"
            sx={{
              backgroundColor: `${color}15`,
              color,
              fontWeight: 600,
              fontSize: '12px',
            }}
          />
        )
      },
    },
    {
      field: 'role',
      headerName: '角色',
      width: 100,
      renderCell: (params) => ROLE_LABELS[(params.value as LoginLogItem['role']) ?? ''] ?? '未知',
    },
  ]

  return (
    <AdminLayout>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          登录记录
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>筛选品牌</InputLabel>
          <Select
            label="筛选品牌"
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
          >
            <MenuItem value="">全部品牌</MenuItem>
            {brands.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          minHeight: 400,
          backgroundColor: '#fff',
          borderRadius: '12px',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FF8C00' }} />
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#fafafa' },
            }}
          />
        )}
      </Box>
    </AdminLayout>
  )
}

export default LoginLogsPage
